import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Axe, Compass, ShieldCheck, Sprout, GraduationCap, ArrowRight, Cpu } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1776799215299-1b46fec4761d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxjb3VudHJ5JTIwZmFybSUyMGZpZWxkJTIwZ29sZGVuJTIwZHVza3xlbnwwfHx8fDE3ODQwOTI2MDB8MA&ixlib=rb-4.1.0&q=85";
const FIELD = "https://images.unsplash.com/photo-1773775098130-0f0631c6de05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxmYXRoZXIlMjBzb24lMjBmaXNoaW5nJTIwbGFrZSUyMGNvdW50cnl8ZW58MHx8fHwxNzg0MDkyNjAwfDA&ixlib=rb-4.1.0&q=85";
const FISH = "https://images.unsplash.com/photo-1693298025144-44309c60e8a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxiYWNrd29vZHMlMjBydXN0aWMlMjBsb2clMjBjYWJpbiUyMGZvcmVzdHxlbnwwfHx8fDE3ODQwOTI2MDB8MA&ixlib=rb-4.1.0&q=85";

const GRADE_LABEL = (g) => (g === "K" ? "K" : g);

export default function Landing() {
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    api.get("/grades").then((r) => setGrades(r.data)).catch(() => {});
  }, []);

  const totalCourses = grades.reduce((a, g) => a + g.course_count, 0);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={HERO} alt="Golden country field at dusk" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/25 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36">
          <div className="max-w-3xl stagger">
            <div className="flex items-center gap-2 border border-primary/40 bg-primary/10 px-3 py-1.5 w-fit">
              <Axe className="h-4 w-4 text-primary" strokeWidth={2.5} />
              <span className="label-caps text-primary">Hornsby Homeschool Curriculum</span>
            </div>
            <h1 className="mt-6 font-head text-5xl font-black leading-[0.95] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
              Academically Strong.<br /><span className="text-primary">Prepared for Life.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              A complete, rugged K-12 curriculum for families raising capable, grounded kids. Real lessons, honest work,
              and an AI tutor that never quits on them.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/pricing" data-testid="hero-enroll-btn">
                <Button size="lg" className="rounded-none label-caps bg-primary px-8 py-6 text-base hover:bg-primary/80">
                  Enroll a Student <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/curriculum/browse" data-testid="hero-browse-btn">
                <Button size="lg" variant="outline" className="rounded-none label-caps border-foreground/30 px-8 py-6 text-base hover:bg-foreground/10">
                  Browse Curriculum
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-border md:grid-cols-4 lg:px-0">
          {[
            { n: "K–12", l: "Full Grade Range" },
            { n: subjects.length || 15, l: "Subjects" },
            { n: totalCourses || 99, l: "Courses" },
            { n: "AI", l: "Built-in Tutor" },
          ].map((s, i) => (
            <div key={i} className="px-6 py-10 text-center">
              <div className="font-head text-4xl font-black text-primary">{s.n}</div>
              <div className="label-caps mt-2 text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE / TETRIS GRID */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-px border border-border bg-border md:grid-cols-3">
          <div className="bg-card p-8 lg:p-10">
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h3 className="mt-5 font-head text-2xl font-bold">Built to Last</h3>
            <p className="mt-3 text-sm text-muted-foreground">Rigorous academics in Math, Language Arts, Science, Bible, and Social Studies — no fluff, no filler.</p>
          </div>
          <div className="bg-card p-8 lg:p-10">
            <Sprout className="h-8 w-8 text-primary" />
            <h3 className="mt-5 font-head text-2xl font-bold">Prepared for Life</h3>
            <p className="mt-3 text-sm text-muted-foreground">Home Economics, Life Skills, Accounting, and Character courses that turn knowledge into capability.</p>
          </div>
          <div className="bg-card p-8 lg:p-10">
            <Cpu className="h-8 w-8 text-primary" />
            <h3 className="mt-5 font-head text-2xl font-bold">AI Tutor Included</h3>
            <p className="mt-3 text-sm text-muted-foreground">A patient GPT-powered tutor with voice and speech — explains, guides, and never gives away the answer.</p>
          </div>
        </div>
      </section>

      {/* GRADE SELECTOR */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <span className="label-caps text-primary">Pick a Grade</span>
              <h2 className="mt-2 font-head text-3xl font-bold sm:text-4xl">Start Where They Are</h2>
            </div>
            <GraduationCap className="hidden h-10 w-10 text-muted-foreground sm:block" />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {grades.map((g) => (
              <Link
                key={g.grade_level}
                to={`/curriculum?grade=${g.grade_level}`}
                data-testid={`grade-chip-${g.grade_level}`}
                className="group flex min-w-[72px] flex-col items-center border border-border bg-background px-5 py-4 transition-colors hover:border-primary hover:bg-primary/10"
              >
                <span className="font-head text-2xl font-black text-foreground group-hover:text-primary">{GRADE_LABEL(g.grade_level)}</span>
                <span className="mt-1 text-[0.65rem] uppercase tracking-wider text-muted-foreground">{g.course_count} courses</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT IMAGE SECTION */}
      <section className="mx-auto grid max-w-7xl items-stretch gap-px border border-border bg-border md:grid-cols-2 my-20 lg:my-28">
        <div className="relative min-h-[340px]">
          <img src={FIELD} alt="Father and child learning outdoors" className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="bg-card p-8 lg:p-14">
          <Compass className="h-8 w-8 text-primary" />
          <h2 className="mt-5 font-head text-3xl font-bold sm:text-4xl leading-tight">A Curriculum That Works Outside the Classroom</h2>
          <p className="mt-4 text-muted-foreground">
            Every course is written to connect the lesson to the real world — the shop, the field, the kitchen, and the woods.
            Enroll one student for a full year in a grade level, or take a flexible monthly membership for the whole family.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/pricing"><Button className="rounded-none label-caps bg-primary hover:bg-primary/80">See Enrollment Options</Button></Link>
            <Link to="/curriculum/browse"><Button variant="outline" className="rounded-none label-caps">Explore Courses</Button></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-border">
        <img src={FISH} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative mx-auto max-w-3xl px-5 py-24 text-center lg:px-8">
          <h2 className="font-head text-4xl font-black sm:text-5xl">Raise Them Strong.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Join the Hornsby family and give your kids an education that prepares them for more than a test.</p>
          <Link to="/register" className="mt-8 inline-block" data-testid="cta-register-btn">
            <Button size="lg" className="rounded-none label-caps bg-primary px-10 py-6 text-base hover:bg-primary/80">Create Your Account</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
